/**
 * oddit-section-how-to-use
 * - Tab switching (morning/night) with proper ARIA
 * - Timeline dot positioning (aligned to step centers)
 * - Theme editor lifecycle support
 */
(function () {
  'use strict';

  function initSection(sectionEl) {
    if (!sectionEl) return null;

    var section = sectionEl.querySelector('.oddit-section-how-to-use') || sectionEl;
    if (!section.classList.contains('oddit-section-how-to-use')) return null;

    var controller = new AbortController();
    var signal = controller.signal;

    /* -----------------------------------------------------------------------
       Tab switching
       ----------------------------------------------------------------------- */
    var tablist = section.querySelector('[role="tablist"]');
    var tabs = section.querySelectorAll('[role="tab"]');
    var panels = section.querySelectorAll('[role="tabpanel"]');

    function activateTab(tab) {
      tabs.forEach(function (t) {
        t.classList.remove('oddit-section-how-to-use__tab--active');
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      });
      tab.classList.add('oddit-section-how-to-use__tab--active');
      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('tabindex', '0');

      var targetPanelId = tab.getAttribute('aria-controls');
      panels.forEach(function (panel) {
        if (panel.id === targetPanelId) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      pauseHiddenVideos();
      resumeActiveVideo();
      autoplayVisiblePanel();
      requestAnimationFrame(function () {
        var ap = section.querySelector('[role="tabpanel"]:not([hidden])');
        if (!ap) return;
        var w = ap.querySelector('.oddit-section-how-to-use__steps-wrapper');
        if (w) {
          w.classList.remove('oddit-section-how-to-use__steps-wrapper--animated');
        }
        requestAnimationFrame(function () {
          positionTimelineDots();
          requestAnimationFrame(function () {
            if (w) triggerTimeline(w);
          });
        });
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener(
        'click',
        function () {
          activateTab(tab);
        },
        { signal: signal },
      );
    });

    // Keyboard navigation for tabs (WAI-ARIA pattern)
    if (tablist) {
      tablist.addEventListener(
        'keydown',
        function (e) {
          var tabArray = Array.prototype.slice.call(tabs);
          var currentIndex = tabArray.indexOf(document.activeElement);
          if (currentIndex === -1) return;

          var newIndex;
          switch (e.key) {
            case 'ArrowRight':
              newIndex = (currentIndex + 1) % tabArray.length;
              break;
            case 'ArrowLeft':
              newIndex = (currentIndex - 1 + tabArray.length) % tabArray.length;
              break;
            case 'Home':
              newIndex = 0;
              break;
            case 'End':
              newIndex = tabArray.length - 1;
              break;
            default:
              return;
          }

          e.preventDefault();
          tabArray[newIndex].focus();
          activateTab(tabArray[newIndex]);
        },
        { signal: signal },
      );
    }

    /* -----------------------------------------------------------------------
       Video playback
       ----------------------------------------------------------------------- */
    var playBtns = section.querySelectorAll('.oddit-section-how-to-use__play-btn');
    var pauseBtns = section.querySelectorAll('.oddit-section-how-to-use__pause-btn');

    function startVideo(videoWrap) {
      if (!videoWrap || videoWrap.classList.contains('oddit-section-how-to-use__video--playing')) return;

      var videoUrl = videoWrap.getAttribute('data-video-url');
      var videoType = videoWrap.getAttribute('data-video-type');
      if (!videoUrl) return;

      var player = window.OdditVideoEmbed.createPlayer(videoUrl, videoType, {
        controls: 'false',
        loop: 'true',
      });
      if (!player) return;

      videoWrap.appendChild(player);
      videoWrap.classList.add('oddit-section-how-to-use__video--playing');
    }

    playBtns.forEach(function (btn) {
      btn.addEventListener(
        'click',
        function () {
          startVideo(btn.closest('.oddit-section-how-to-use__video'));
        },
        { signal: signal },
      );
    });

    pauseBtns.forEach(function (btn) {
      btn.addEventListener(
        'click',
        function () {
          var videoWrap = btn.closest('.oddit-section-how-to-use__video');
          if (!videoWrap) return;

          var video = videoWrap.querySelector('video');
          if (!video) return;

          if (video.paused) {
            video.play().catch(function () {});
            btn.classList.remove('oddit-section-how-to-use__pause-btn--paused');
            btn.setAttribute('aria-label', 'Pause video');
          } else {
            video.pause();
            btn.classList.add('oddit-section-how-to-use__pause-btn--paused');
            btn.setAttribute('aria-label', 'Play video');
          }
        },
        { signal: signal },
      );
    });

    function autoplayVisiblePanel() {
      var activePanel = section.querySelector('[role="tabpanel"]:not([hidden])');
      if (!activePanel) return;
      var videoWrap = activePanel.querySelector('.oddit-section-how-to-use__video[data-autoplay="true"]');
      if (videoWrap) startVideo(videoWrap);
    }

    function pauseHiddenVideos() {
      panels.forEach(function (panel) {
        if (!panel.hasAttribute('hidden')) return;
        var video = panel.querySelector('.oddit-section-how-to-use__video video');
        if (video && !video.paused) {
          video.pause();
          video.dataset.autoPaused = 'true';
        }
      });
    }

    function resumeActiveVideo() {
      var activePanel = section.querySelector('[role="tabpanel"]:not([hidden])');
      if (!activePanel) return;
      var video = activePanel.querySelector('.oddit-section-how-to-use__video video');
      if (!video || !video.paused || video.dataset.autoPaused !== 'true') return;

      delete video.dataset.autoPaused;
      video.play().catch(function () {});

      var pauseBtn = activePanel.querySelector('.oddit-section-how-to-use__pause-btn');
      if (pauseBtn) {
        pauseBtn.classList.remove('oddit-section-how-to-use__pause-btn--paused');
        pauseBtn.setAttribute('aria-label', 'Pause video');
      }
    }

    var autoplayObserver = null;
    var hasAutoplay = !!section.querySelector('[data-autoplay="true"]');

    if (hasAutoplay && typeof IntersectionObserver !== 'undefined') {
      autoplayObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              autoplayVisiblePanel();
              autoplayObserver.disconnect();
              autoplayObserver = null;
            }
          });
        },
        { rootMargin: '200px 0px' },
      );
      autoplayObserver.observe(section);
    } else if (hasAutoplay) {
      autoplayVisiblePanel();
    }

    /* -----------------------------------------------------------------------
       Timeline dot positioning
       Dots align vertically with the center of each step card.
       ----------------------------------------------------------------------- */
    function positionTimelineDots() {
      var activePanel = section.querySelector('[role="tabpanel"]:not([hidden])');
      if (!activePanel) return;

      var dots = activePanel.querySelectorAll('.oddit-section-how-to-use__timeline-dot');
      var steps = activePanel.querySelectorAll('.oddit-section-how-to-use__step');
      var line = activePanel.querySelector('.oddit-section-how-to-use__timeline-line');

      if (dots.length === 0 || steps.length === 0) return;

      steps.forEach(function (step, index) {
        if (!dots[index]) return;
        var numberEl = step.querySelector('.oddit-section-how-to-use__step-number');
        var dotTop;
        if (numberEl) {
          dotTop = step.offsetTop + numberEl.offsetTop + numberEl.offsetHeight / 2 - 6;
        } else {
          dotTop = step.offsetTop + step.offsetHeight / 2 - 6;
        }
        dots[index].style.top = dotTop + 'px';
        dots[index].classList.add('oddit-section-how-to-use__timeline-dot--positioned');
      });

      if (line) {
        line.style.top = '0';
        line.style.bottom = '0';
      }
    }

    // Position dots on load and resize
    positionTimelineDots();

    // Use ResizeObserver for reliable repositioning when layout changes
    var resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(function () {
        positionTimelineDots();
      });
      section.querySelectorAll('.oddit-section-how-to-use__steps-list').forEach(function (list) {
        resizeObserver.observe(list);
      });
    }

    // Fallback: reposition after a short delay for lazy-loaded CSS
    setTimeout(positionTimelineDots, 200);
    setTimeout(positionTimelineDots, 600);

    var resizeTimer;
    function debouncedResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(positionTimelineDots, 100);
    }

    window.addEventListener('resize', debouncedResize, { signal: signal });

    // Reposition after fonts load
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(positionTimelineDots);
    }

    // Reposition after images load (step product images affect layout)
    section.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', positionTimelineDots, { signal: signal });
      }
    });

    // Reposition when lazy-loaded CSS finishes loading
    var cssLink = document.querySelector('link[href*="oddit-section-how-to-use.css"]');
    if (cssLink && cssLink.media === 'print') {
      cssLink.addEventListener(
        'load',
        function () {
          requestAnimationFrame(positionTimelineDots);
        },
        { signal: signal },
      );
    }

    /* -----------------------------------------------------------------------
       Timeline entrance animation
       Line draws top→bottom, dots pop in with spring, steps slide up
       ----------------------------------------------------------------------- */
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setStaggerIndices(wrapper) {
      var dots = wrapper.querySelectorAll('.oddit-section-how-to-use__timeline-dot--positioned');
      var stepEls = wrapper.querySelectorAll('.oddit-section-how-to-use__step');
      for (var i = 0; i < dots.length; i++) {
        dots[i].style.setProperty('--htu-index', i);
      }
      for (var i = 0; i < stepEls.length; i++) {
        stepEls[i].style.setProperty('--htu-index', i);
      }
    }

    function triggerTimeline(wrapper) {
      if (!wrapper) return;
      positionTimelineDots();
      setStaggerIndices(wrapper);
      wrapper.classList.add('oddit-section-how-to-use__steps-wrapper--animated');
    }

    var timelineObserver = null;
    if (!reducedMotion && typeof IntersectionObserver !== 'undefined') {
      timelineObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            triggerTimeline(entries[i].target);
            timelineObserver.unobserve(entries[i].target);
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.01 },
      );

      var initialPanel = section.querySelector('[role="tabpanel"]:not([hidden])');
      if (initialPanel) {
        var initialWrapper = initialPanel.querySelector('.oddit-section-how-to-use__steps-wrapper');
        if (initialWrapper) {
          timelineObserver.observe(initialWrapper);
        }
      }
    } else if (!reducedMotion) {
      var fallbackPanel = section.querySelector('[role="tabpanel"]:not([hidden])');
      if (fallbackPanel) {
        triggerTimeline(fallbackPanel.querySelector('.oddit-section-how-to-use__steps-wrapper'));
      }
    }

    return {
      destroy: function () {
        controller.abort();
        clearTimeout(resizeTimer);
        if (resizeObserver) resizeObserver.disconnect();
        if (autoplayObserver) autoplayObserver.disconnect();
        if (timelineObserver) timelineObserver.disconnect();
      },
    };
  }

  /* -----------------------------------------------------------------------
     Initialize on page load
     ----------------------------------------------------------------------- */
  var instances = new Map();

  function initAll() {
    document.querySelectorAll('.oddit-section-how-to-use').forEach(function (el) {
      var sectionWrapper = el.closest('.shopify-section') || el;
      if (!instances.has(sectionWrapper)) {
        var instance = initSection(sectionWrapper);
        if (instance) instances.set(sectionWrapper, instance);
      }
    });
  }

  initAll();

  /* -----------------------------------------------------------------------
     Theme editor lifecycle
     ----------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (e) {
    var instance = initSection(e.target);
    if (instance) instances.set(e.target, instance);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var instance = instances.get(e.target);
    if (instance) {
      instance.destroy();
      instances.delete(e.target);
    }
  });

  document.addEventListener('shopify:section:select', function (e) {
    var sectionWrapper = e.target;
    var existing = instances.get(sectionWrapper);
    if (existing) existing.destroy();
    var instance = initSection(sectionWrapper);
    if (instance) instances.set(sectionWrapper, instance);
  });
})();
